import React, {useEffect, useRef, useState} from "react";
import gql from "graphql-tag";
import {apolloClient, makeApolloClientCall} from "../../Api/makeApolloApiCall";
import {LoadingWrapper} from '../Components/LoadingWrapper';
import ReactTable from '../Components/ReactTable';
import {makeApiCall} from "../../Api/makeApiCall";
import SelectDropdown from "../Components/Dropdowns/SelectDropdown";
import SummarizedMultiSelect from "./Components/SummarizedMultiSelect";
import {wrapValueInOptionStructure} from "../../utils/maybeAddLabelToOptions";
import {AVAILABLE_PROJECTION_VERSIONS_QUERY} from "./Components/VersionSelect";
import {fetchStoreUniversePages, pageOffsetsFrom, STORE_MAINTENANCE_PAGE_SIZE} from "./storeUniversePaging";
import CanonicalStoreOverrideModal from "./Components/CanonicalStoreOverrideModal";
import StoreUniverseLoadStatus from "./Components/StoreUniverseLoadStatus";
import CanonicalStoreSearchSelect from "./Components/CanonicalStoreSearchSelect";
import {useProjectionLoadTimer} from "./Components/ProjectionLoadTimer";
import {convertToQueryParams} from "../../utils/urlBuilder";
import {
    buildInflationFactorMap,
    formatRecommendedUniverse,
    fromUniverseName,
    isRecommendedSample,
    loadApprovedUniverseOptions,
    lookupInflationFactor,
    NUMBER_EXPRESSION_COLUMN,
    toUniverseValues,
} from "./Components/storeUniverseTableHelpers";
import {
    buildCanonicalStoreOverridesMap,
    canonicalStoreOverridesUrl,
    OVERRIDABLE_FIELDS,
    UNIVERSE_OVERRIDE_FIELDS,
} from "./canonicalStoreOverrides";
import {faIcons} from "../../utils/iconConstants";

export const storeSourceOptions = [
    'CHAIN_STORE_GUIDE',
    'SPINS',
    'INFRA',
    'MANUAL',
    'C_AND_S',
].map(source => ({label: source.replaceAll('_', ' '), value: source}));

export const hasAcvAnchorFilterOptions = [
    {label: 'All', value: 'ALL'},
    {label: 'Has ACV Anchor', value: 'YES'},
    {label: 'No ACV Anchor', value: 'NO'},
];

const graphqlEnumListArg = (argName, values) =>
    values?.length ? `${argName}: [${values.join(', ')}]` : undefined;

const storeUniverseQueryArgs = ({version, offset, limit, sourceFilter, universeFilter, hasAcvAnchorFilter, canonicalStoreFilter}) => [
    `version: "${version}"`,
    `limit: ${limit}`,
    `offset: ${offset}`,
    graphqlEnumListArg('sourceFilter', sourceFilter),
    graphqlEnumListArg('universeFilter', universeFilter),
    hasAcvAnchorFilter === 'YES' && 'filters: {has_acv_anchor: true}',
    hasAcvAnchorFilter === 'NO' && 'filters: {has_acv_anchor: false}',
    canonicalStoreFilter && `canonicalStoreIdFilter: ["${canonicalStoreFilter}"]`,
].filter(Boolean).join(', ');

const storeUniversePageGraphqlQuery = (storeFilters, offset, limit) => ` query {
            storeUniverse(${storeUniverseQueryArgs({...storeFilters, offset, limit})}) {
                totalItemsCount
                items {
                  id
                  canonicalStoreId
                  canonicalStoreTag
                  source
                  bannerName
                  storeName
                  sourceStoreId
                  address
                  city
                  state
                  postalCode
                  classification
                  recommendedClassification
                  openDate
                  closedDate
                  hasAcvAnchor
                  universe
                  recommendedCensusCell { cellId }
                  recommendedStandardCell { cellId }
                  recommendedUniverse
                }
            }
    } `;


const queryStoreUniversePageTolerantly = (graphqlQuery) =>
    apolloClient.query({
        query: gql` ${graphqlQuery} `,
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
    }).then((response) => response.data);

export const loadStoreUniverseBatch = (version, storeFilters = {}, offset = 0, apolloQuery = queryStoreUniversePageTolerantly) =>
    apolloQuery(storeUniversePageGraphqlQuery({version, ...storeFilters}, offset, STORE_MAINTENANCE_PAGE_SIZE))
        .then(response => ({
            items: response?.storeUniverse?.items ?? [],
            totalItemsCount: response?.storeUniverse?.totalItemsCount ?? 0,
        }));

const savedStoreValuesByTag = (stores) =>
    stores.reduce((acc, store) => ({
        ...acc,
        [store.canonicalStoreTag]: {
            openDate: store.openDate ?? '',
            closedDate: store.closedDate ?? '',
            ...(store.classification !== undefined && {classification: store.classification?.toUpperCase() ?? ''}),
            universe: store.universes.map(fromUniverseName),
        },
    }), {});

const withSavedOverrides = (overridesMap, records, removed) => {
    const removedTags = new Set(removed.map(record => record.canonicalStoreTag));
    const kept = Object.fromEntries(Object.entries(overridesMap ?? {}).filter(([tag]) => !removedTags.has(tag)));
    return {...kept, ...buildCanonicalStoreOverridesMap(records)};
};

const UNIVERSE_COLUMN_ID = 'universe';
const STORE_OVERRIDE_CELL_CLASS_NAME = 'store-override-cell';

const overriddenColumnIds = (override) => [
    ...OVERRIDABLE_FIELDS.filter(field => override?.[field]),
    ...(UNIVERSE_OVERRIDE_FIELDS.some(field => override?.[field]?.length) ? [UNIVERSE_COLUMN_ID] : []),
];

export const STORE_OVERRIDE_EDIT_BUTTON_TEST_ID = 'store-override-edit-button';
const STORE_OVERRIDE_EDIT_TITLE = 'Store Overrides';
export const STORE_OVERRIDES_NOT_LOADED_TITLE = 'Store overrides could not be loaded';

const mergeStoreUniverseItems = (items, {inflationFactorMap}) =>
    items.map(item => {
        const recommendedInflationFactor = lookupInflationFactor(
            inflationFactorMap,
            item.recommendedUniverse,
            item.classification,
        );
        return {
            ...item,
            recommendedUniverseValues: toUniverseValues(item.recommendedUniverse),
            recommendedStandardCellId: item.recommendedStandardCell?.cellId ?? '',
            recommendedCensusCellId: item.recommendedCensusCell?.cellId ?? '',
            recommendedSample: isRecommendedSample(item) ? 'Yes' : 'No',
            recommendedInflationFactor,
            acvAnchored: item.hasAcvAnchor ? 'Yes' : 'No',
        };
    });

const appendUnloadedRows = (loadedRows, newRows) => {
    const loadedIds = new Set(loadedRows.map(row => row.id));
    return [...loadedRows, ...newRows.filter(row => !loadedIds.has(row.id))];
};

const batchProgressAfter = (offset, {items, totalItemsCount}, pageStride = items.length) => {
    const nextOffset = offset + items.length;
    return {
        nextOffset,
        totalItemsCount,
        pageStride,
        nextBatchSize: items.length ? Math.min(STORE_MAINTENANCE_PAGE_SIZE, Math.max(totalItemsCount - nextOffset, 0)) : 0,
    };
};

export const LOAD_MORE_STORES_ERROR = 'Failed to load more stores';
export const LOAD_ALL_STORES_ERROR = 'Failed to load all stores';
export const STORE_UNIVERSE_LOAD_PROGRESS_TEST_ID = 'store-universe-load-progress';

export const ProjectionMaintenanceStoreTab = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const [projectionVersionOptions, setProjectionVersionOptions] = useState([]);
    const [projectionVersion, setProjectionVersion] = useState(undefined);
    const [approvedUniverseOptions, setApprovedUniverseOptions] = useState([]);

    const [sourceFilter, setSourceFilter] = useState([]);
    const [universeFilter, setUniverseFilter] = useState([]);
    const [hasAcvAnchorFilter, setHasAcvAnchorFilter] = useState('ALL');
    const [canonicalStoreOption, setCanonicalStoreOption] = useState(null);

    const [canonicalStoreOverrides, setCanonicalStoreOverrides] = useState(null);
    const [overrideUniverseOptions, setOverrideUniverseOptions] = useState([]);
    const [overrideStore, setOverrideStore] = useState(null);
    const [submittedFilters, setSubmittedFilters] = useState(null);
    const {startLoad, finishLoad, cancelLoad} = useProjectionLoadTimer();
    const [batchProgress, setBatchProgress] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadMoreError, setLoadMoreError] = useState(null);
    const [loadAllProgress, setLoadAllProgress] = useState(null);
    const lookupsRef = useRef(null);
    const requestIdRef = useRef(0);


    useEffect(() => {
        Promise.all([
            makeApolloClientCall(AVAILABLE_PROJECTION_VERSIONS_QUERY),
        ])
            .then(([
                       projectionVersionsResponse
                   ]) => {
                setProjectionVersionOptions(projectionVersionsResponse.availableProjectionVersions.map(version => wrapValueInOptionStructure(version.version) || []));
            });
    }, []);

    useEffect(() => {
        if(!projectionVersion) return

        setApprovedUniverseOptions([]);
        loadApprovedUniverseOptions(projectionVersion)
            .then(setApprovedUniverseOptions);
    }, [projectionVersion]);

    const onSubmit = () => {
        setSubmittedFilters({
            projectionVersion,
            sourceFilter,
            universeFilter,
            hasAcvAnchorFilter,
            canonicalStoreFilter: canonicalStoreOption?.value,
        });
    };

    useEffect(() => {
        if (!submittedFilters?.projectionVersion) return;

        const requestId = ++requestIdRef.current;
        setLoading(true);
        setBatchProgress(null);
        setLoadingMore(false);
        setLoadMoreError(null);
        setLoadAllProgress(null);
        setCanonicalStoreOverrides(null);
        setOverrideUniverseOptions([]);
        setOverrideStore(null);
        startLoad('Stores tab');

        loadApprovedUniverseOptions(submittedFilters.projectionVersion)
            .then(options => {
                if (requestId !== requestIdRef.current) return;
                setOverrideUniverseOptions(options);
            });

        Promise.all([
            loadStoreUniverseBatch(submittedFilters.projectionVersion, submittedFilters),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/inflationFactors/`),
            makeApiCall(canonicalStoreOverridesUrl(submittedFilters.projectionVersion)),
        ]).then(([
                     batch,
                     inflationFactorsResponse,
                     canonicalStoreOverridesResponse,
                 ]) => {
            if (requestId !== requestIdRef.current) return;
            finishLoad(batch.items.length);
            setCanonicalStoreOverrides(canonicalStoreOverridesResponse?.apiError
                ? null
                : buildCanonicalStoreOverridesMap(canonicalStoreOverridesResponse?.data ?? []));
            lookupsRef.current = {
                inflationFactorMap: buildInflationFactorMap(inflationFactorsResponse.data),
            };
            setBatchProgress(batchProgressAfter(0, batch));
            setData(mergeStoreUniverseItems(batch.items, lookupsRef.current));
        }).finally(() => {
            if (requestId !== requestIdRef.current) return;
            cancelLoad();
            setLoading(false);
        });
    }, [submittedFilters]);

    const onLoadMore = () => {
        const requestId = requestIdRef.current;
        const offset = batchProgress.nextOffset;
        setLoadingMore(true);
        setLoadMoreError(null);
        startLoad('Stores tab');

        const failLoadMore = () => {
            cancelLoad();
            setLoadMoreError(LOAD_MORE_STORES_ERROR);
        };

        loadStoreUniverseBatch(submittedFilters.projectionVersion, submittedFilters, offset)
            .then(batch => {
                if (requestId !== requestIdRef.current) return;
                if (!batch.items.length) {
                    failLoadMore();
                    return;
                }
                const mergedRows = mergeStoreUniverseItems(batch.items, lookupsRef.current);
                finishLoad(appendUnloadedRows(data, mergedRows).length);
                setBatchProgress(batchProgressAfter(offset, batch));
                setData(prevData => appendUnloadedRows(prevData, mergedRows));
            })
            .catch(() => {
                if (requestId !== requestIdRef.current) return;
                failLoadMore();
            })
            .finally(() => {
                if (requestId !== requestIdRef.current) return;
                setLoadingMore(false);
            });
    };

    const onLoadAll = () => {
        const requestId = requestIdRef.current;
        const {nextOffset, totalItemsCount, pageStride} = batchProgress;
        setLoading(true);
        setLoadMoreError(null);
        setLoadAllProgress({loadedItemsCount: nextOffset, totalItemsCount});
        startLoad('Stores tab');

        fetchStoreUniversePages({
            fetchPage: (offset) => loadStoreUniverseBatch(submittedFilters.projectionVersion, submittedFilters, offset),
            offsets: pageOffsetsFrom(nextOffset, totalItemsCount, pageStride),
            pageSize: STORE_MAINTENANCE_PAGE_SIZE,
            totalItemsCount,
            loadedItemsCount: nextOffset,
            onProgress: (progress) => {
                if (requestId !== requestIdRef.current) return;
                setLoadAllProgress(progress);
            },
        })
            .then(items => {
                if (requestId !== requestIdRef.current) return;
                const mergedRows = mergeStoreUniverseItems(items, lookupsRef.current);
                finishLoad(appendUnloadedRows(data, mergedRows).length);
                setBatchProgress(batchProgressAfter(nextOffset, {items, totalItemsCount}, pageStride));
                setData(prevData => appendUnloadedRows(prevData, mergedRows));
            })
            .catch(() => {
                if (requestId !== requestIdRef.current) return;
                cancelLoad();
                setLoadMoreError(LOAD_ALL_STORES_ERROR);
            })
            .finally(() => {
                if (requestId !== requestIdRef.current) return;
                setLoading(false);
                setLoadAllProgress(null);
            });
    };

    const onOverrideSaved = ({records = [], removed = [], stores = []}) => {
        const valuesByTag = savedStoreValuesByTag(stores);
        setCanonicalStoreOverrides(previous => withSavedOverrides(previous, records, removed));
        setData(prevData => prevData?.map(row =>
            valuesByTag[row.canonicalStoreTag] ? {...row, ...valuesByTag[row.canonicalStoreTag]} : row
        ));
        setOverrideStore(null);
    };

    const storeOverrideAction = (row) => row.canonicalStoreId &&
        <button type={'button'}
                className={'store-override-edit-button'}
                title={canonicalStoreOverrides ? STORE_OVERRIDE_EDIT_TITLE : STORE_OVERRIDES_NOT_LOADED_TITLE}
                data-testid={STORE_OVERRIDE_EDIT_BUTTON_TEST_ID}
                disabled={!canonicalStoreOverrides}
                onClick={() => setOverrideStore(row)}
        >
            <i className={faIcons.edit}/>
        </button>;

    const columns = [
        {
            header: 'ACTIONS',
            id: 'actions',
            sticky: 'left',
            size: 100,
            cell: ({row}) => storeOverrideAction(row.original),
        },
        {
            header: 'BANNER',
            accessorKey: 'bannerName',
            sticky: 'left',
            size: 250,
            cell: ({row}) => (
                <div className={'banner-name-cell'}>
                    {row.original.canonicalStoreId ?
                        <a
                            className={'store-linkage-action-button'}
                            title={'Linked source records'}
                            data-testid={'store-linkage-action-button'}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                            href={`/storeLinkage?${convertToQueryParams({
                                canonicalStoreId: row.original.canonicalStoreId,
                                storeName: row.original.storeName || row.original.bannerName,
                            })}`}
                        >
                            {row.original.bannerName}
                        </a> :
                        row.original.bannerName
                    }
                </div>
            ),
        },
        {header: 'STORE NAME', accessorKey: 'storeName', size: 250, sticky: 'left'},
        {header: 'SOURCE', accessorKey: 'source', sticky: 'left'},
        {header: 'STORE ID', accessorKey: 'sourceStoreId', sticky: 'left'},
        {header: 'SOURCE RECORD ID', accessorKey: 'id', size: 280},
        {header: 'CANONICAL STORE ID', accessorKey: 'canonicalStoreId', size: 250,},
        {header: 'ADDRESS', accessorKey: 'address'},
        {header: 'OPEN DATE', accessorKey: 'openDate'},
        {header: 'CLOSED DATE', accessorKey: 'closedDate'},
        {header: 'ACV ANCHORED', accessorKey: 'acvAnchored', filterFn: 'includesString'},
        {header: 'UNIVERSE', id: UNIVERSE_COLUMN_ID, accessorFn: row => formatRecommendedUniverse(row.universe), size: 192, filterFn: 'includesString'},
        {header: 'CLASSIFICATION', accessorKey: 'classification', filterFn: 'includesString'},
        {header: 'STANDARD CELL', accessorKey: 'recommendedStandardCellId', ...NUMBER_EXPRESSION_COLUMN},
        {header: 'CENSUS CELL', accessorKey: 'recommendedCensusCellId', ...NUMBER_EXPRESSION_COLUMN},
        {header: 'SAMPLE', accessorKey: 'recommendedSample', filterFn: 'includesString'},
        {header: 'INFLATION FACTOR', accessorKey: 'recommendedInflationFactor', size: 168, ...NUMBER_EXPRESSION_COLUMN},
    ];

    return (
        <div className={"projection-maintenance-store-contents"}>
            <div className={'projection-new-stores-toolbar'}>
                <div className={'projection-new-stores-projection-factor-version'} data-testid={'projection_factors_version'}>
                    <SelectDropdown id={"projection_version_dropdown"}
                                    className={"optional-dropdown"}
                                    dataTestId={"projection_version"}
                                    inputLabel={"Projection Version"}
                                    selectValue={projectionVersion}
                                    options={projectionVersionOptions}
                                    onChange={(option) => {
                                        setProjectionVersion(option?.value);
                                        setCanonicalStoreOption(null);
                                    }}
                    />
                </div>

                <SummarizedMultiSelect id={"source_filter_dropdown"}
                                       dataTestId={"source_filter"}
                                       inputLabel={"Source"}
                                       summaryNoun={"sources"}
                                       placeholder={"All sources"}
                                       selectValues={sourceFilter}
                                       options={storeSourceOptions}
                                       onChange={(options) => setSourceFilter(options?.map(o => o.value) ?? [])}
                />

                <SummarizedMultiSelect id={"universe_filter_dropdown"}
                                       dataTestId={"universe_filter"}
                                       inputLabel={"Universe"}
                                       summaryNoun={"universes"}
                                       placeholder={"All universes"}
                                       selectValues={universeFilter}
                                       options={approvedUniverseOptions}
                                       onChange={(options) => setUniverseFilter(options?.map(o => o.value) ?? [])}
                />

                <SelectDropdown id={"has_acv_anchor_filter_dropdown"}
                                className={"optional-dropdown"}
                                dataTestId={"has_acv_anchor_filter"}
                                inputLabel={"ACV Anchor"}
                                selectLabel={hasAcvAnchorFilterOptions.find(o => o.value === hasAcvAnchorFilter)?.label}
                                selectValue={hasAcvAnchorFilter}
                                options={hasAcvAnchorFilterOptions}
                                onChange={(option) => setHasAcvAnchorFilter(option?.value ?? 'ALL')}
                />

                <div className={'id-input canonical-store-filter'} data-testid={'canonical_store_filter'}>
                    <label className={'input-group-label'} htmlFor={'canonical_store_filter_search'}>Store:</label>
                    <CanonicalStoreSearchSelect inputId={'canonical_store_filter_search'}
                                                version={projectionVersion}
                                                value={canonicalStoreOption}
                                                onChange={setCanonicalStoreOption}
                                                isDisabled={!projectionVersion}
                    />
                </div>

                <button type={'button'}
                        className={'button primary projection-new-stores-submit-button'}
                        data-testid={'projection-new-stores-submit-button'}
                        disabled={!projectionVersion}
                        title={projectionVersion ? 'Load stores for the selected filters' : 'Select a Projection Version first'}
                        onClick={onSubmit}
                >
                    Submit
                </button>
            </div>

            {submittedFilters && !loading && batchProgress &&
                <StoreUniverseLoadStatus loadedCount={data?.length ?? 0}
                                         totalItemsCount={batchProgress.totalItemsCount}
                                         nextBatchSize={batchProgress.nextBatchSize}
                                         loadingMore={loadingMore}
                                         error={loadMoreError}
                                         onLoadMore={onLoadMore}
                                         onLoadAll={onLoadAll}
                />}

            {loading && loadAllProgress &&
                <div className={'callout primary small store-universe-load-progress'} data-testid={STORE_UNIVERSE_LOAD_PROGRESS_TEST_ID}>
                    {loadAllProgress.loadedItemsCount.toLocaleString('en-US')} stores out of {loadAllProgress.totalItemsCount.toLocaleString('en-US')} total records
                </div>}

            {submittedFilters && <LoadingWrapper loading={loading}>
                <ReactTable
                    className="projection-new-stores-table"
                    filterable
                    hasStickyHeader
                    data={data ?? []}
                    columns={columns}
                    initialState={{pagination: {pageSize: 10}}}
                    saveSortStateToUrl
                    savePageIndexToUrl
                    getTdProps={(row, table, columnId) =>
                        overriddenColumnIds(canonicalStoreOverrides?.[row.original.canonicalStoreTag]).includes(columnId)
                            ? STORE_OVERRIDE_CELL_CLASS_NAME
                            : ''}
                />
            </LoadingWrapper>}

            {overrideStore &&
                <CanonicalStoreOverrideModal version={submittedFilters.projectionVersion}
                                             store={overrideStore}
                                             override={canonicalStoreOverrides?.[overrideStore.canonicalStoreTag]}
                                             universeOptions={overrideUniverseOptions}
                                             onClose={() => setOverrideStore(null)}
                                             onSaved={onOverrideSaved}
                />}
        </div>
    );
};
